import { useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { ScrollArea } from "./components/ui/scroll-area";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import {
  Send,
  FileText,
  Bot,
  Settings,
  Loader2,
  Upload,
  Trash2,
  ArrowLeft,
  RefreshCw,
  Cpu,
  Brain,
  Network,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Target,
  BookOpen,
  CheckCircle,
  Lightbulb,
  User,
  ExternalLink,
  Mail,
  Beaker,
  Layers,
  Sparkles,
  Maximize2,
  Minimize2,
  Briefcase
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Where the standalone "paste your resume, get curated live job matches"
// tool is hosted. Kept as one constant so it only has to change in one
// place if the deployment ever moves.
const CAREER_RADAR_URL = "https://career-search-pied.vercel.app";

// Reads the context Career Radar's "Practice full interview" deep link
// passes via query string (?source=career-radar&company=...&role=...&
// source_url=...&match_category=...&match_reason=...&gap_summary=...&
// seed_question=...). Only recognized when source=career-radar is present,
// so a direct visit to this app with no query string behaves exactly as
// before. No resume content ever travels in this URL — only the job
// posting's own metadata and Career Radar's fit/gap reasoning about it.
function readCareerRadarContext() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("source") !== "career-radar") return null;
  const company = params.get("company") || "";
  const role = params.get("role") || "";
  if (!company && !role) return null;
  return {
    company,
    role,
    sourceUrl: params.get("source_url") || "",
    matchCategory: params.get("match_category") || "",
    matchReason: params.get("match_reason") || "",
    gapSummary: params.get("gap_summary") || "",
    seedQuestion: params.get("seed_question") || "",
  };
}

// Built only for a Career Radar arrival, which skips the "paste your
// resume" step entirely (see startCareerRadarInterview below). The backend
// resume_expert prompt always expects a {resume_context} value — without
// this, an empty session falls back to its own "no resume was submitted,
// ask the candidate to paste one" placeholder, which would make the model
// stall instead of evaluating the candidate's answer. This stands in for
// an actual resume using the job-fit context Career Radar already showed
// the candidate, and explicitly tells the model not to fabricate resume
// quotes it doesn't have.
function buildCareerRadarResumeContext(ctx) {
  const roleLine = [ctx.role, ctx.company].filter(Boolean).join(" at ");
  return [
    "No resume text was submitted for this session — the candidate arrived directly from Career Radar, a tool that already matched their real resume against this specific job posting (the resume itself never left that tool).",
    roleLine ? `Target role: ${roleLine}` : null,
    ctx.matchReason ? `Why Career Radar matched them to this role: ${ctx.matchReason}` : null,
    ctx.gapSummary ? `Where Career Radar flagged a possible gap: ${ctx.gapSummary}` : null,
    "",
    `The interview already opened with this exact question (shown to the candidate verbatim, not generated by you), built from the gap above: "${ctx.seedQuestion}"`,
    "",
    "Evaluate the candidate's answer on its own merits — depth, ownership, specificity — using the role and gap context above. You do not have their literal resume text, so never fabricate or quote a \"resume line.\" In the evaluation format below, treat \"Resume claim being probed\" as the role requirement or gap you're probing instead, and keep follow-up questions grounded in their actual answers plus the gap noted above."
  ].filter(Boolean).join("\n");
}

// Every expert persona's evaluations come back from the model as Markdown
// (bold labels, "---" dividers, bullet lists), but chat messages were
// rendered as plain text — so the candidate saw literal "**" and "-"
// characters instead of bold labels and bullets. This is a small,
// dependency-free renderer (no new package.json/yarn.lock entry) covering
// just the patterns these prompts actually produce: **bold**/__bold__,
// *italic*/_italic_, `code`, "- "/"* " and "1. " lists, "---" rules, and
// "#"-style headings. It intentionally doesn't attempt full CommonMark.
function renderInlineMarkdown(text) {
  const nodes = [];
  const pattern = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("**") || token.startsWith("__")) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(
        <code key={key++} className="bg-slate-900/60 px-1 py-0.5 rounded text-[0.85em]">
          {token.slice(1, -1)}
        </code>
      );
    } else {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderMarkdown(content) {
  const lines = content.split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listItems = [];
  let listOrdered = false;
  let key = 0;

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    blocks.push(
      <p key={key++} className="mb-2 last:mb-0">
        {paragraphLines.map((line, i) => (
          <span key={i}>
            {renderInlineMarkdown(line)}
            {i < paragraphLines.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const items = listItems;
    const ordered = listOrdered;
    blocks.push(
      ordered ? (
        <ol key={key++} className="mb-2 last:mb-0 pl-5 space-y-0.5 list-decimal">
          {items.map((item, i) => <li key={i}>{renderInlineMarkdown(item)}</li>)}
        </ol>
      ) : (
        <ul key={key++} className="mb-2 last:mb-0 pl-5 space-y-0.5 list-disc">
          {items.map((item, i) => <li key={i}>{renderInlineMarkdown(item)}</li>)}
        </ul>
      )
    );
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed === "") {
      flushParagraph();
      flushList();
      continue;
    }
    if (/^-{3,}$/.test(trimmed)) {
      flushParagraph();
      flushList();
      blocks.push(<hr key={key++} className="my-2 border-slate-700" />);
      continue;
    }
    const headingMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push(
        <p key={key++} className="text-sm font-semibold mb-1">
          {renderInlineMarkdown(headingMatch[2])}
        </p>
      );
      continue;
    }
    const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
    const numberedMatch = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      if (listOrdered) flushList();
      listOrdered = false;
      listItems.push(bulletMatch[1]);
      continue;
    }
    if (numberedMatch) {
      flushParagraph();
      if (!listOrdered) flushList();
      listOrdered = true;
      listItems.push(numberedMatch[1]);
      continue;
    }
    flushList();
    paragraphLines.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

// Simple Expert Chat Panel
const ExpertChatPanel = ({
  type,
  name,
  subtitle,
  icon: Icon,
  color,
  bgColor,
  expertType,
  suggestedQuestions,
  requiresResume,
  careerRadarContext,
  isActive,
  isCollapsed,
  onToggleExpand
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [resumeDraft, setResumeDraft] = useState("");
  const [resumeSubmitted, setResumeSubmitted] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Resume-based panel waits until the candidate pastes a resume before a
    // session is created, so the resume text can be attached at creation
    // time — EXCEPT for a Career Radar arrival, which skips that step
    // entirely (see startCareerRadarInterview below) and is ready to answer
    // immediately, same as the other two panels.
    if (!requiresResume) {
      createSession();
    } else if (careerRadarContext?.seedQuestion) {
      startCareerRadarInterview();
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

const createSession = async () => {
    try {
      const response = await axios.post(`${API}/sessions`, { expert_type: expertType });
      setSessionId(response.data.session_id);
      setMessages([]);
      setInterviewStarted(false);
      setShowCustomInput(false);
    } catch (error) {
      console.error("Failed to create session");
    }
  };

  // Resume-based panel only: create the session WITH the pasted resume
  // attached, then immediately ask the model for a first personalized
  // question so the candidate never has to type a bootstrap message.
  // (Direct visits only — a Career Radar arrival uses
  // startCareerRadarInterview below instead.)
  const startResumeInterview = async () => {
    if (!resumeDraft.trim() || loading) return;
    setLoading(true);
    try {
      const sessionResp = await axios.post(`${API}/sessions`, {
        expert_type: expertType,
        resume_context: resumeDraft
      });
      const newSessionId = sessionResp.data.session_id;
      setSessionId(newSessionId);
      setResumeSubmitted(true);
      setInterviewStarted(true);
      setShowCustomInput(true);
      setMessages([]);

      const chatResp = await axios.post(`${API}/chat`, {
        session_id: newSessionId,
        expert_type: expertType,
        message: "Please begin the interview with your first question, personalized to the resume I just submitted."
      });
      setMessages([{
        id: Date.now().toString(),
        role: "assistant",
        content: chatResp.data.response,
        hasContext: chatResp.data.retrieved_context?.length > 0
      }]);
    } catch (error) {
      toast.error("Could not start the resume-based interview. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // When this panel arrives via Career Radar's "Practice full interview"
  // deep link, the opening question is already decided — Career Radar's
  // own interviewer_seed_question — and the job-fit context Career Radar
  // already showed the candidate is enough to ground the interview. There
  // is no separate "paste your resume" step here at all: this runs
  // automatically on mount (and again from "New Chat") rather than waiting
  // on a button click. It shows the exact seed question immediately and
  // enables the answer input right away, while the real session is created
  // in the background — sendMessage() and the Input below both gate on
  // sessionId, so nothing is lost if the candidate answers before that
  // network call resolves; the input is simply disabled until it does.
  const startCareerRadarInterview = async () => {
    if (!careerRadarContext) return;
    setMessages([{
      id: "career-radar-seed-question",
      role: "assistant",
      content: `**Interview Question:**\n\n${careerRadarContext.seedQuestion}\n\n*Answer below to continue the interview.*`
    }]);
    setInterviewStarted(true);
    setShowCustomInput(true);
    setSessionId(null);
    try {
      const sessionResp = await axios.post(`${API}/sessions`, {
        expert_type: expertType,
        resume_context: buildCareerRadarResumeContext(careerRadarContext)
      });
      setSessionId(sessionResp.data.session_id);
    } catch (error) {
      toast.error("Could not set up this interview session. Try New Chat to retry.");
    }
  };

  // Start interview by showing question as AI message (waits for user response)
  const startInterview = (question) => {
    const interviewerQuestion = {
      id: Date.now().toString(),
      role: "assistant",
      content: `**Interview Question:**\n\n${question}\n\n*Please provide your answer below.*`
    };
    setMessages([interviewerQuestion]);
    setInterviewStarted(true);
    setShowCustomInput(true);
  };

  const sendMessage = async (text) => {
    const messageText = text || input;
    if (!messageText.trim() || !sessionId || loading) return;

    const userMessage = { id: Date.now().toString(), role: "user", content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        expert_type: expertType,
        message: messageText
      });
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.data.response,
        hasContext: response.data.retrieved_context?.length > 0
      }]);
    } catch (error) {
      toast.error("Failed to get response");
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    if (careerRadarContext) {
      // No resume-paste step to reset back to here — just re-run the same
      // auto-start used on mount, which re-shows the seed question and
      // opens a fresh session right away.
      setSessionId(null);
      setMessages([]);
      setInterviewStarted(false);
      setShowCustomInput(false);
      startCareerRadarInterview();
    } else if (requiresResume) {
      setSessionId(null);
      setMessages([]);
      setInterviewStarted(false);
      setShowCustomInput(false);
      setResumeSubmitted(false);
      setResumeDraft("");
    } else {
      createSession();
    }
  };

  if (isCollapsed) {
    return (
      <button
        onClick={onToggleExpand}
        title={`Expand ${name}`}
        className="w-full h-full flex lg:flex-col items-center justify-center gap-2 lg:gap-2 rounded-xl border border-slate-800 bg-slate-900/60 hover:bg-slate-800/80 transition-colors relative px-3 py-2"
      >
        <div className={`p-2 rounded-lg ${bgColor}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <span className="text-xs lg:text-[10px] text-slate-300 font-medium text-center leading-tight">
          {name}
        </span>
        {(messages.length > 0 || careerRadarContext) && (
          <span
            className={`absolute top-1.5 right-1.5 lg:right-auto lg:left-1/2 lg:translate-x-3 w-2 h-2 rounded-full ${
              careerRadarContext ? "bg-emerald-400" : "bg-slate-500"
            }`}
          />
        )}
      </button>
    );
  }

  return (
    <Card
      className={`flex flex-col h-full bg-slate-900/60 border-slate-800 overflow-hidden ${
        careerRadarContext ? "ring-2 ring-emerald-500/50 ring-offset-2 ring-offset-slate-950" : ""
      }`}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b border-slate-800 ${bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${bgColor}`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">{name}</h3>
              <p className="text-xs text-slate-400">{subtitle || "AI Interview Expert"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleExpand}
              className="text-slate-400 hover:text-white h-8 w-8 p-0"
              title={isActive ? "Back to 3-panel view" : "Expand this panel"}
            >
              {isActive ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetChat}
              className="text-slate-400 hover:text-white h-8 w-8 p-0"
              title="New Chat"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        <div className="space-y-4">
          {!requiresResume && messages.length === 0 && !interviewStarted && (
            <div className="text-center py-6">
              <Icon className={`w-12 h-12 mx-auto mb-4 ${color} opacity-50`} />
              <p className="text-slate-400 text-sm mb-4">Start a conversation with {name}</p>

              {/* Prebuilt Questions - Click to start interview with that question */}
              <div className="space-y-2 mb-6">
                {suggestedQuestions.map((q, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => startInterview(q)}
                    className="w-full text-left justify-start whitespace-normal break-words leading-snug text-xs border-slate-700 bg-slate-800/50 hover:bg-slate-800 text-slate-300 h-auto py-2 px-3"
                  >
                    {q}
                  </Button>
                ))}
              </div>
              {/* Start Mock Interview Button - Random question */}
              <Button
                onClick={() => {
                  const randomQuestion = suggestedQuestions[Math.floor(Math.random() * suggestedQuestions.length)];
                  startInterview(randomQuestion);
                }}
                className={`w-full ${bgColor} hover:opacity-90 text-white font-semibold py-3 mb-4`}
                disabled={loading}
              >
                <Target className="w-4 h-4 mr-2" />
                Start Mock Interview
              </Button>

              {/* Ask your own question button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCustomInput(true)}
                className="text-slate-400 hover:text-white text-xs"
              >
                Or ask your own question...
              </Button>
            </div>
          )}

          {/* Career Radar context card — a persistent header, not a gate.
              Shown as soon as the panel mounts and stays put above the
              conversation; it never blocks the question or the answer box
              below (see startCareerRadarInterview — there's no resume-paste
              step to wait on here at all). */}
          {careerRadarContext && (
            <div className="mb-2 max-w-sm mx-auto text-left bg-emerald-500/10 border border-emerald-600/40 rounded-lg p-3">
              <p className="text-emerald-400 text-xs font-semibold flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" />
                Preparing from Career Radar
              </p>
              <p className="text-slate-200 text-sm mt-1 font-medium">
                {[careerRadarContext.role, careerRadarContext.company].filter(Boolean).join(" at ")}
              </p>
              {careerRadarContext.gapSummary && (
                <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                  {careerRadarContext.gapSummary}
                </p>
              )}
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[90%] rounded-xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-800/80 text-slate-200 border border-slate-700'
                }`}
              >
                <div className="text-sm leading-relaxed">{renderMarkdown(msg.content)}</div>
                {msg.hasContext && (
                  <p className={`text-xs mt-2 ${color} opacity-70`}>
                    ✓ Enhanced with knowledge base
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* Resume paste prompt — direct visits only. A Career Radar
              arrival never shows this: it has no resume to paste and skips
              straight to the question + answer box above/below. */}
          {requiresResume && !careerRadarContext && !resumeSubmitted && (
            <div className="text-center py-6">
              <Icon className={`w-12 h-12 mx-auto mb-4 ${color} opacity-50`} />
              <p className="text-slate-300 text-sm font-medium mb-1">
                This panel interviews YOU — on your own resume.
              </p>
              <p className="text-slate-500 text-xs mb-4 max-w-sm mx-auto">
                Paste your resume below. Every question here is generated from specific lines in it — named companies, projects, and metrics — never a generic question bank.
              </p>
              <textarea
                value={resumeDraft}
                onChange={(e) => setResumeDraft(e.target.value)}
                placeholder="Paste your resume text here..."
                rows={8}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder:text-slate-500 p-3 mb-3 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <Button
                onClick={startResumeInterview}
                disabled={loading || !resumeDraft.trim()}
                className={`w-full ${bgColor} hover:opacity-90 text-white font-semibold py-3`}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Target className="w-4 h-4 mr-2" />
                )}
                Generate My First Question
              </Button>
              <p className="text-slate-600 text-[11px] mt-3 leading-relaxed">
                Tip: run this same resume through{" "}
                <a
                  href={CAREER_RADAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 underline hover:text-emerald-300"
                >
                  Career Radar
                </a>{" "}
                to see live job matches curated to these exact skills.
              </p>
            </div>
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-800/80 border border-slate-700 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className={`w-4 h-4 ${color} animate-spin`} />
                  <span className="text-sm text-slate-400">Thinking...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area - Only show when interview started or custom input enabled */}
      {showCustomInput && (
        <div className="p-3 border-t border-slate-800 bg-slate-900/80">
          <div className="flex gap-2">
            <Input
              data-testid={`${type}-input`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder={
                !sessionId
                  ? "Setting up your session..."
                  : interviewStarted ? "Type your answer..." : "Ask your own question..."
              }
              className="flex-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 text-sm"
              disabled={loading || !sessionId}
              autoFocus
            />
            <Button
              data-testid={`${type}-send`}
              onClick={() => sendMessage()}
              disabled={loading || !input.trim() || !sessionId}
              className={`${bgColor} hover:opacity-90 px-4`}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// Main Dashboard
const Dashboard = () => {
  const navigate = useNavigate();
  const [showGuide, setShowGuide] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [activeExpert, setActiveExpert] = useState(null);
  // Set once on mount from the incoming query string (see
  // readCareerRadarContext above) — null on a normal, direct visit.
  const [careerRadarContext] = useState(() => readCareerRadarContext());

  useEffect(() => {
    if (careerRadarContext) {
      const target = [careerRadarContext.role, careerRadarContext.company].filter(Boolean).join(" at ");
      toast.success(`Loaded from Career Radar — ${target || "your matched role"} is ready in the Resume-Based panel below.`);
    }
  }, []);

  const handleToggleExpand = (type) => {
    setActiveExpert((prev) => (prev === type ? null : type));
  };

  // Three panels, left to right: combined technical prep, then the
  // resume-personalized flow in the center, then HR/behavioral/executive
  // on the right. Order here IS left-to-right render order below.
  const experts = [
    {
      type: "technical",
      name: "Technical Interview",
      subtitle: "Data Science · ML · LLM · GenAI/RAG",
      icon: Cpu,
      color: "text-blue-400",
      bgColor: "bg-blue-600",
      expertType: "technical_expert",
      suggestedQuestions: [
        "How do you design an A/B test, and how do you know when to trust the result?",
        "What is the bias-variance tradeoff, and how does it actually show up in production models?",
        "Explain attention and KV caching — why does KV caching matter for inference cost?",
        "Walk me through designing a RAG pipeline: chunking, retrieval, and the ways it fails."
      ]
    },
    {
      type: "resume",
      name: "Resume-Based Interview",
      subtitle: "Built from YOUR resume, not a template",
      icon: FileText,
      color: "text-emerald-400",
      bgColor: "bg-emerald-600",
      expertType: "resume_expert",
      requiresResume: true,
      suggestedQuestions: []
    },
    {
      type: "hr",
      name: "HR, Behavioral & Executive",
      subtitle: "STAR · probability · executive presence",
      icon: User,
      color: "text-amber-400",
      bgColor: "bg-amber-600",
      expertType: "hr_expert",
      suggestedQuestions: [
        "Tell me about a time you disagreed with a decision your manager made. What did you do?",
        "Estimate how many AI/ML job postings are open across the US right now — walk me through your reasoning.",
        "Describe a project that failed. What was your role, and what would you do differently?",
        "How do you prioritize when three stakeholders each think their request is the top priority?"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header */}
      <header className="px-6 py-3 border-b border-slate-800 bg-slate-900/50 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Bot className="w-7 h-7 text-blue-500" />
            <h1 className="text-xl font-bold text-white">TriVectorAI</h1>
            <span className="text-slate-500 text-sm hidden sm:inline">Interview Simulator</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(CAREER_RADAR_URL, "_blank", "noopener,noreferrer")}
              className="border-emerald-700/60 text-emerald-400 hover:text-white hover:bg-emerald-700"
              title="Opens Career Radar in a new tab"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Job Search — Curated From Your Resume
              <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowAbout(!showAbout); setShowGuide(false); }}
              className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <User className="w-4 h-4 mr-2" />
              About
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowGuide(!showGuide); setShowAbout(false); }}
              className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              How to Use
              {showGuide ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      </header>

      {/* How to Use Guide - Collapsible */}
      {showGuide && (
        <div className="bg-slate-900/80 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">
                  How to Use TriVectorAI – Interview Simulator
                </h2>

                <p className="text-slate-300 mb-4">
                  Welcome to the <span className="text-blue-400 font-semibold">Three-Panel AI Interview Experience</span>
                </p>

                <p className="text-slate-400 text-sm mb-6">
                  TriVectorAI simulates a real-world interview loop across three connected panels:
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <Cpu className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <span className="text-blue-400 font-medium">Technical Interview (Left)</span>
                      <p className="text-slate-400 text-sm">Data Science, ML foundations, LLM systems, and GenAI/RAG — one interviewer covering all four, moving between them the way a real panel round does.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <FileText className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <span className="text-emerald-400 font-medium">Resume-Based Interview (Center)</span>
                      <p className="text-slate-400 text-sm">Paste your resume here. Every question is pulled from specific lines in it and checks whether your answers hold up to follow-up.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <User className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <span className="text-amber-400 font-medium">HR, Behavioral & Executive (Right)</span>
                      <p className="text-slate-400 text-sm">STAR-method behavioral questions, probability/estimation brain-teasers, and executive-presence scenarios.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-blue-400" />
                    Getting Started
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-slate-300 font-medium">1. Start on the Left or the Right</p>
                      <p className="text-slate-500">Pick one of the curated questions on the Technical or HR/Behavioral panel, or let it pick a random one for you.</p>
                    </div>
                    <div>
                      <p className="text-slate-300 font-medium">2. Bring Your Own Resume to the Center</p>
                      <p className="text-slate-500">Paste your resume into the center panel to get questions generated from your own experience — named projects, named metrics, not generic prep.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div>
                <div className="space-y-4">
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-emerald-400" />
                      3. Wait for the Evaluation
                    </h3>
                    <p className="text-slate-400 text-sm mb-2">Whichever panel you're talking to will:</p>
                    <ul className="text-slate-500 text-sm space-y-1">
                      <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-400" /> Score your answer (0–10)</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-400" /> Identify strengths & weaknesses</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-400" /> Detect conceptual gaps</li>
                      <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-400" /> Ask a deeper follow-up question</li>
                    </ul>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                      4. Then Go Find the Job
                    </h3>
                    <p className="text-slate-500 text-sm">
                      Once you've practiced, take the same resume to{" "}
                      <a
                        href={CAREER_RADAR_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-400 underline hover:text-emerald-300"
                      >
                        Career Radar <ExternalLink className="w-3 h-3 inline" />
                      </a>{" "}
                      — it curates live job postings matched to the exact skills you were just interviewed on, instead of a generic job-board feed.
                    </p>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      5. Best Practice
                    </h3>
                    <p className="text-slate-400 text-sm mb-2">Treat this like a real interview:</p>
                    <ul className="text-slate-500 text-sm space-y-1">
                      <li>• Avoid vague statements</li>
                      <li>• Define technical terms</li>
                      <li>• Explain architecture clearly</li>
                      <li>• Discuss trade-offs and edge cases</li>
                    </ul>
                    <p className="text-slate-400 text-sm mt-2 italic">The deeper your answer, the more advanced the follow-up will be.</p>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500/10 to-violet-500/10 rounded-lg p-4 border border-blue-500/20">
                    <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-violet-400" />
                      6. The Goal
                    </h3>
                    <p className="text-slate-400 text-sm mb-2">This isn't about getting a perfect score. It's about:</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="border-blue-500/30 text-blue-400">Identifying blind spots</Badge>
                      <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">Improving structured thinking</Badge>
                      <Badge variant="outline" className="border-amber-500/30 text-amber-400">Interview-ready storytelling</Badge>
                      <Badge variant="outline" className="border-violet-500/30 text-violet-400">Training for the real thing</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowGuide(false)}
                className="text-slate-500 hover:text-slate-300"
              >
                <ChevronUp className="w-4 h-4 mr-2" />
                Hide Guide
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* About the Builder Section */}
      {showAbout && (
        <div className="bg-slate-900/80 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">About the Builder</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Why I Built TriVectorAI */}
                <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-400" />
                    Why I Built TriVectorAI
                  </h3>
                  <div className="text-slate-400 text-sm space-y-3">
                    <p>I built TriVectorAI out of <span className="text-white">curiosity first</span>.</p>
                    <p>Over the past few years, I've spent a lot of time exploring LLMs, RAG pipelines, and multi-agent systems. One thing became very clear to me:</p>
                    <p className="text-slate-300 italic">We're interacting with increasingly powerful AI systems — but we're not always training ourselves to think at the same level of structure and depth.</p>
                    <div className="bg-slate-900/50 rounded-lg p-3 border-l-2 border-amber-500">
                      <p className="text-slate-300">Most tools let you ask questions.</p>
                      <p className="text-slate-300">Very few tools <span className="text-amber-400 font-medium">evaluate how you think</span>.</p>
                      <p className="text-slate-400 mt-2">That difference matters.</p>
                    </div>
                    <p>TriVectorAI was my attempt to create something that doesn't just respond — it <span className="text-white">challenges, scores, and pushes deeper</span>.</p>
                  </div>
                </div>

                {/* Thinking in Three Dimensions */}
                <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-400" />
                    Thinking in Three Dimensions
                  </h3>
                  <div className="text-slate-400 text-sm space-y-3">
                    <p>When I experiment with AI systems, I naturally think in layers:</p>
                    <ul className="space-y-2 ml-2">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>The <span className="text-blue-400">technical depth layer</span> — how it actually works</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                        <span>The <span className="text-emerald-400">personal layer</span> — how it applies to what you've actually done</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                        <span>The <span className="text-amber-400">human layer</span> — how you communicate and lead under ambiguity</span>
                      </li>
                    </ul>
                    <p>So I designed TriVectorAI the same way.</p>
                    <p className="text-slate-300">Three panels. Three cognitive perspectives. <span className="text-white font-medium">One integrated experience.</span></p>
                    <div className="grid grid-cols-1 gap-2 mt-3">
                      <div className="flex items-start gap-2 p-2 bg-blue-500/10 rounded border border-blue-500/20">
                        <Cpu className="w-4 h-4 text-blue-400 mt-0.5" />
                        <span className="text-xs"><span className="text-blue-400">Technical</span> — Data Science, ML foundations, LLM systems, and GenAI/RAG in one continuous panel.</span>
                      </div>
                      <div className="flex items-start gap-2 p-2 bg-emerald-500/10 rounded border border-emerald-500/20">
                        <FileText className="w-4 h-4 text-emerald-400 mt-0.5" />
                        <span className="text-xs"><span className="text-emerald-400">Resume-Based</span> — questions generated from your own resume, not a template.</span>
                      </div>
                      <div className="flex items-start gap-2 p-2 bg-amber-500/10 rounded border border-amber-500/20">
                        <User className="w-4 h-4 text-amber-400 mt-0.5" />
                        <span className="text-xs"><span className="text-amber-400">HR & Executive</span> — behavioral, probability, and executive-presence rounds.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Part of AI Labs */}
                <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Beaker className="w-5 h-5 text-violet-400" />
                    Part of a Bigger Exploration — AI Labs
                  </h3>
                  <div className="text-slate-400 text-sm space-y-3">
                    <p>TriVectorAI is one of many experiments inside <span className="text-violet-400 font-medium">AI Labs</span>.</p>
                    <p>AI Labs is where I build and test practical AI systems:</p>
                    <div className="grid grid-cols-2 gap-2 my-3">
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                        <span>Retrieval pipelines</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                        <span>Multi-agent workflows</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                        <span>Evaluation frameworks</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                        <span>Real-world GenAI apps</span>
                      </div>
                    </div>
                    <p>I believe <span className="text-white">experimentation is the best way to truly understand AI</span>.</p>
                    <div className="bg-slate-900/50 rounded-lg p-3 text-slate-300 italic">
                      Reading about RAG is one thing.<br/>
                      Designing and stress-testing it is another.
                    </div>
                    <p>AI Labs is my public sandbox for that journey.</p>
                  </div>
                </div>

                {/* Follow the Journey */}
                <div className="bg-gradient-to-br from-blue-500/10 to-violet-500/10 rounded-xl p-5 border border-blue-500/20">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    Follow the Journey
                  </h3>
                  <div className="text-slate-400 text-sm space-y-3">
                    <p>If you're interested in:</p>
                    <div className="flex flex-wrap gap-2 my-3">
                      <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">LLM system design</Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">RAG optimization</Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">Agentic AI patterns</Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">Applied AI experiments</Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">Practical AI insights</Badge>
                    </div>
                    <p>I share deeper breakdowns and structured analysis in my newsletter:</p>
                    <a
                      href="https://www.vasanthnatarajan.me/newsletter"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 mt-4 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition-colors"
                    >
                      <Sparkles className="w-4 h-4" />
                      Subscribe to Newsletter
                      <ExternalLink className="w-4 h-4 ml-auto" />
                    </a>
                    <p className="text-xs text-slate-500 mt-3">
                      Everything I build — including TriVectorAI — is part of a broader effort to explore AI thoughtfully and practically.
                    </p>
                    <p className="text-slate-300 text-xs">If that resonates with you, feel free to follow along.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAbout(false)}
                className="text-slate-500 hover:text-slate-300"
              >
                <ChevronUp className="w-4 h-4 mr-2" />
                Hide About
              </Button>
            </div>
          </div>
        </div>
      )}

{/* Expert Panels */}
      <main className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 text-center">
            <h2 className="text-lg text-slate-300 mb-1">Three panels, one interview loop</h2>
            <p className="text-sm text-slate-500 max-w-3xl mx-auto">
              {activeExpert ? (
                "Click the maximize icon again to return to the 3-panel view"
              ) : (
                <>
                  <span className="text-blue-400 font-medium">Left</span> — technical depth across Data Science, ML, LLMs, GenAI &amp; RAG.{" "}
                  <span className="text-emerald-400 font-medium">Center</span> — paste your resume and get questions built from it.{" "}
                  <span className="text-amber-400 font-medium">Right</span> — HR, behavioral, probability &amp; executive questions. Click the expand icon to focus on one panel, and use the{" "}
                  <span className="text-emerald-400 font-medium">Job Search</span> button above once you're ready to see roles curated to the same skills.
                </>
              )}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 min-h-[560px] lg:h-[calc(100vh-200px)]">
            {experts.map((expert) => {
              const isActive = activeExpert === expert.type;
              const isCollapsed = activeExpert !== null && !isActive;
              return (
                <div
                  key={expert.type}
                  className={`min-w-0 transition-all duration-300 ${
                    isCollapsed
                      ? "order-2 w-full h-20 lg:h-full lg:w-24 shrink-0"
                      : "flex-1 order-1"
                  }`}
                >
                  <ExpertChatPanel
                    {...expert}
                    careerRadarContext={expert.type === "resume" ? careerRadarContext : null}
                    isActive={isActive}
                    isCollapsed={isCollapsed}
                    onToggleExpand={() => handleToggleExpand(expert.type)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};;

// Admin Page
const AdminPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState({
    technical_expert: [],
    hr_expert: []
  });
  const [uploading, setUploading] = useState({});

  // Resume-Based has no shared knowledge base to manage here — its
  // "knowledge" is whatever resume a candidate pastes into that panel at
  // interview time, so it's intentionally left out of this grid.
  const experts = [
    { key: 'technical_expert', name: 'Technical Interview', color: 'blue' },
    { key: 'hr_expert', name: 'HR, Behavioral & Executive', color: 'amber' }
  ];

  useEffect(() => {
    fetchAllDocuments();
  }, []);

  const fetchAllDocuments = async () => {
    const newDocs = {};
    for (const expert of experts) {
      try {
        const response = await axios.get(`${API}/documents/${expert.key}`);
        newDocs[expert.key] = response.data.documents || [];
      } catch {
        newDocs[expert.key] = [];
      }
    }
    setDocuments(newDocs);
  };

  const handleUpload = async (expertKey, files) => {
    if (!files?.length) return;
    setUploading(prev => ({ ...prev, [expertKey]: true }));

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        await axios.post(`${API}/documents/upload?expert_type=${expertKey}`, formData);
        toast.success(`Uploaded: ${file.name}`);
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }

    setUploading(prev => ({ ...prev, [expertKey]: false }));
    fetchAllDocuments();
  };

  const handleDelete = async (expertKey, docId, filename) => {
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success(`Deleted: ${filename}`);
      fetchAllDocuments();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="px-6 py-4 border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white">Knowledge Base</h1>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {experts.map((expert) => (
            <Card key={expert.key} className="bg-slate-900/60 border-slate-800">
              <CardContent className="p-4">
                <h3 className={`font-semibold text-${expert.color}-400 mb-4`}>{expert.name}</h3>

                {/* Upload */}
                <label className={`block border-2 border-dashed border-slate-700 rounded-lg p-6 text-center cursor-pointer hover:border-${expert.color}-500/50 transition-colors mb-4`}>
                  <input
                    type="file"
                    accept=".txt,.md,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUpload(expert.key, e.target.files)}
                  />
                  {uploading[expert.key] ? (
                    <Loader2 className="w-6 h-6 mx-auto text-slate-400 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-400">Drop files here</p>
                    </>
                  )}
                </label>

                {/* Documents */}
                <div className="space-y-2 max-h-48 overflow-auto">
                  {documents[expert.key]?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No documents</p>
                  ) : (
                    documents[expert.key]?.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-300 truncate">{doc.filename}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(expert.key, doc.id, doc.filename)}
                          className="text-slate-500 hover:text-red-400 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Resume-Based has no shared knowledge base — explained here instead of a third upload box */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <h3 className="font-semibold text-emerald-400 mb-4">Resume-Based Interview</h3>
              <div className="border-2 border-dashed border-slate-800 rounded-lg p-6 text-center">
                <FileText className="w-6 h-6 mx-auto text-slate-500 mb-2" />
                <p className="text-sm text-slate-500">
                  No shared knowledge base here by design — each candidate pastes their own
                  resume directly into that panel at interview time, so there's nothing to
                  upload in advance.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
            {/* Footer */}
<footer className="py-6 px-4 border-t border-slate-800 mt-auto">
  <div className="max-w-7xl mx-auto text-center">
    <p className="text-slate-400 text-sm mb-2">
      © 2026 TriVector AI. All rights reserved.
    </p>
    <p className="text-slate-600 text-xs max-w-2xl mx-auto">
      This website, its design, content, and underlying application logic are protected by copyright and intellectual property laws. Unauthorized reproduction, distribution, or replication is strictly prohibited.
    </p>
  </div>
</footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" theme="dark" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
